import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, humanise, num, pct } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';

interface Ad {
  id: string;
  title: string;
  imageUrl: string;
  mediaType: 'IMAGE' | 'VIDEO';
  videoUrl: string | null;
  targetUrl: string | null;
  placement: string;
  categoryId: string | null;
  locationId: string | null;
  startsAt: string;
  endsAt: string;
  sortOrder: number;
  isActive: boolean;
  impressions: number;
  clicks: number;
}

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

/** The category tree as one list, children indented under their parent. */
function flatten(nodes: CategoryNode[] | undefined, depth = 0): Array<{ value: string; label: string }> {
  return (nodes ?? []).flatMap((n) => [
    { value: n.id, label: `${'\u2003'.repeat(depth)}${n.name}` },
    ...flatten(n.children, depth + 1),
  ]);
}

/** Picks a file, uploads it, and hands back its address. */
function UploadButton({ accept, label, onUploaded }: { accept: string; label: string; onUploaded: (url: string) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <label className="btn btn--ghost btn--sm" style={{ cursor: busy ? 'progress' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {busy ? 'Uploading…' : label}
      <input
        type="file"
        accept={accept}
        hidden
        disabled={busy}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          try {
            const r = await api.upload<{ url: string }>('/ads/media', file);
            onUploaded(r.url);
            toast('Uploaded', 'success');
          } catch (err) {
            toast((err as Error).message, 'error');
          } finally {
            setBusy(false);
          }
        }}
      />
    </label>
  );
}

// HOME_BANNER is the carousel at the top of Home and HOME_POPUP a poster over
// it, once a day: both show the whole image. The others are strips and crop.
const PLACEMENTS = ['HOME_BANNER', 'HOME_BAND', 'HOME_HIGHLIGHTS', 'HOME_POPUP', 'CATEGORY_BAND', 'SEARCH_INLINE', 'LISTING_DETAIL'];

/** v2 §6 — the advertisement bands between home category blocks. */
export function AdsPage() {
  const [editing, setEditing] = useState<{ row?: Ad } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ['ads'], queryFn: () => api.get<Ad[]>('/ads/admin') });

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api.patch(`/ads/${id}`, body) : api.post('/ads', body),
    onSuccess: () => {
      toast('Advertisement saved', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['ads'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const now = Date.now();

  return (
    <div className="stack">
      <Notice tone="info">
        Creatives run between home category blocks — visible, but not interrupting the feed (v2 §6). An advertisement
        with no category or location set shows to everyone.
      </Notice>

      <Card flush>
        <CardHead
          title="Advertisements"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({})}>
              + New advertisement
            </Button>
          }
        />
        {isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 130 }} />
          </div>
        ) : !data?.length ? (
          <Empty icon="◫" title="No advertisements" body="Create one to fill the home ad band." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Creative</th>
                  <th>Placement</th>
                  <th>Runs</th>
                  <th style={{ textAlign: 'right' }}>Impressions</th>
                  <th style={{ textAlign: 'right' }}>Clicks</th>
                  <th style={{ textAlign: 'right' }}>CTR</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((ad) => {
                  const live = ad.isActive && new Date(ad.startsAt).getTime() <= now && new Date(ad.endsAt).getTime() > now;
                  return (
                    <tr key={ad.id}>
                      <td>
                        <div className="row">
                          {ad.imageUrl ? (
                            <img className="thumb" src={ad.imageUrl} alt="" loading="lazy" />
                          ) : (
                            <video className="thumb" src={ad.videoUrl ?? undefined} muted preload="metadata" />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div className="cell-strong cell-clip">{ad.title}</div>
                            {ad.targetUrl && <div className="cell-muted cell-clip">{ad.targetUrl}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        {humanise(ad.placement)}
                        {ad.mediaType === 'VIDEO' && <div className="cell-muted">Video</div>}
                      </td>
                      <td className="cell-muted">
                        {date(ad.startsAt)} → {date(ad.endsAt)}
                      </td>
                      <td className="cell-num">{num(ad.impressions)}</td>
                      <td className="cell-num">{num(ad.clicks)}</td>
                      <td className="cell-num">{ad.impressions ? `${pct(ad.clicks, ad.impressions)}%` : '—'}</td>
                      <td>
                        <Badge tone={live ? 'green' : ad.isActive ? 'amber' : 'neutral'}>
                          {live ? 'Running' : ad.isActive ? 'Scheduled' : 'Paused'}
                        </Badge>
                      </td>
                      <td>
                        <div className="cell-actions">
                          <Button size="sm" onClick={() => setEditing({ row: ad })}>
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <AdEditor
          row={editing.row}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: editing.row?.id, body })}
        />
      )}
    </div>
  );
}

function AdEditor({
  row,
  onClose,
  onSave,
  saving,
}: {
  row?: Ad;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const iso = (v: string | undefined, fallbackDays: number) =>
    v ? v.slice(0, 10) : new Date(Date.now() + fallbackDays * 86_400_000).toISOString().slice(0, 10);

  const categories = useQuery({ queryKey: ['categories-tree'], queryFn: () => api.get<CategoryNode[]>('/categories?tree=true') });
  const [form, setForm] = useState({
    title: row?.title ?? '',
    mediaType: row?.mediaType ?? 'IMAGE',
    videoUrl: row?.videoUrl ?? '',
    categoryId: row?.categoryId ?? '',
    imageUrl: row?.imageUrl ?? '',
    targetUrl: row?.targetUrl ?? '',
    placement: row?.placement ?? 'HOME_BAND',
    startsAt: iso(row?.startsAt, 0),
    endsAt: iso(row?.endsAt, 30),
    sortOrder: String(row?.sortOrder ?? 0),
    isActive: row?.isActive === false ? 'false' : 'true',
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal
      title={row ? 'Edit advertisement' : 'New advertisement'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!form.title.trim() || (form.mediaType === 'VIDEO' ? !form.videoUrl.trim() : !form.imageUrl.trim())}
            onClick={() =>
              onSave({
                title: form.title.trim(),
                mediaType: form.mediaType,
                imageUrl: form.imageUrl.trim(),
                videoUrl: form.mediaType === 'VIDEO' ? form.videoUrl.trim() : undefined,
                categoryId: form.categoryId || null,
                targetUrl: form.targetUrl.trim() || undefined,
                placement: form.placement,
                startsAt: new Date(form.startsAt).toISOString(),
                endsAt: new Date(`${form.endsAt}T23:59:59`).toISOString(),
                sortOrder: Number(form.sortOrder) || 0,
                isActive: form.isActive === 'true',
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Title">
          <Input value={form.title} autoFocus onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label="Type">
          <Select
            value={form.mediaType}
            onChange={(e) => set('mediaType', e.target.value)}
            options={[
              { value: 'IMAGE', label: 'Picture' },
              { value: 'VIDEO', label: 'Video' },
            ]}
          />
        </Field>
        {form.mediaType === 'VIDEO' && (
          <Field label="Video" hint="MP4, WebM or MOV, up to 50 MB. Plays muted in the ad's place.">
            <div className="row" style={{ gap: 'var(--s2)' }}>
              <UploadButton accept="video/mp4,video/webm,video/quicktime" label="Upload video" onUploaded={(url) => set('videoUrl', url)} />
              <span className="cell-muted cell-clip">{form.videoUrl ? 'Video uploaded' : 'No video yet'}</span>
            </div>
            {form.videoUrl && (
              <video src={form.videoUrl} muted controls style={{ width: '100%', maxHeight: 160, borderRadius: 'var(--r-chip)', marginTop: 8 }} />
            )}
          </Field>
        )}
        <Field
          label={form.mediaType === 'VIDEO' ? 'Cover picture (optional)' : 'Picture'}
          hint="Upload one, or paste a link to a picture already online. JPG, PNG, WebP or GIF, up to 5 MB."
        >
          <div className="row" style={{ gap: 'var(--s2)' }}>
            <UploadButton accept="image/jpeg,image/png,image/webp,image/gif" label="Upload picture" onUploaded={(url) => set('imageUrl', url)} />
            <Input value={form.imageUrl} onChange={(e) => set('imageUrl', e.target.value)} placeholder="or https://…" />
          </div>
        </Field>
        {form.imageUrl && (
          <img
            src={form.imageUrl}
            alt=""
            style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 'var(--r-chip)' }}
          />
        )}
        <Field label="Target URL (optional)" hint="Where a tap goes. Leave blank for a non-clickable banner.">
          <Input value={form.targetUrl} onChange={(e) => set('targetUrl', e.target.value)} />
        </Field>
        <Field
          label="Category (optional)"
          hint="Category bands, search and listing pages in this category show it. Home shows ads with no category."
        >
          <Select
            value={form.categoryId}
            placeholder="Every category"
            onChange={(e) => set('categoryId', e.target.value)}
            options={flatten(categories.data)}
          />
        </Field>
        <div className="row">
          <Field label="Placement">
            <Select
              value={form.placement}
              onChange={(e) => set('placement', e.target.value)}
              options={PLACEMENTS.map((p) => ({ value: p, label: humanise(p) }))}
            />
          </Field>
          <Field label="Sort order">
            <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </Field>
        </div>
        <div className="row">
          <Field label="Starts">
            <Input type="date" value={form.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
          </Field>
          <Field label="Ends">
            <Input type="date" value={form.endsAt} onChange={(e) => set('endsAt', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select
              value={form.isActive}
              onChange={(e) => set('isActive', e.target.value)}
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Paused' },
              ]}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
